import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import ScratchCard from '../components/ScratchCard';
import { useLanguage, LangToggle } from '../contexts/LanguageContext';
import './Game.css';

const IMAGE_BASE_URL = process.env.REACT_APP_ASSET_BASE_URL || window.location.origin;
const FINAL_REVEAL_HOLD_MS = 2500;

const PRIZE_IMAGES = {
  bouteille_nature_power: 'BOUTEILLE NATURE POWER.jpg.jpeg',
  bouteille: 'BOUTEILLE NATURE POWER.jpg.jpeg',
  casquette: 'CASQUETTE LARGE BORD NATURE POWER.png',
  casquette_large_bord: 'CASQUETTE LARGE BORD NATURE POWER.png',
  polo: 'polo nature power.jpeg',
  polo_nature_power: 'polo nature power.jpeg',
  bloc_note: 'SIMULATION BLOC NOTE NATURE POWER.png',
  bloc_notes: 'SIMULATION BLOC NOTE NATURE POWER.png',
  try_again: null,
  reessayer: null,
};

const LABEL_IMAGE_MAP = {
  'bouteille nature power': 'BOUTEILLE NATURE POWER.jpg.jpeg',
  'bouteille reaktor nature power': 'BOUTEILLE NATURE POWER.jpg.jpeg',
  'bouteille': 'BOUTEILLE NATURE POWER.jpg.jpeg',
  'casquette large bord nature power': 'CASQUETTE LARGE BORD NATURE POWER.png',
  'casquette large bord': 'CASQUETTE LARGE BORD NATURE POWER.png',
  'casquette': 'CASQUETTE LARGE BORD NATURE POWER.png',
  'polo nature power': 'polo nature power.jpeg',
  'polo': 'polo nature power.jpeg',
  'bloc note nature power': 'SIMULATION BLOC NOTE NATURE POWER.png',
  'bloc notes nature power': 'SIMULATION BLOC NOTE NATURE POWER.png',
  'bloc note': 'SIMULATION BLOC NOTE NATURE POWER.png',
  'bloc notes': 'SIMULATION BLOC NOTE NATURE POWER.png',
};

const normalizeKey = (value) => {
  if (!value) return '';
  return value.toString().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ').trim();
};

const normalizeAssetUrl = (value) => {
  if (!value) return null;
  const trimmed = value.toString().trim();
  if (!trimmed) return null;
  if (/^(https?:)?\/\//i.test(trimmed) || trimmed.startsWith('/') || trimmed.startsWith('data:')) return trimmed;
  return `${IMAGE_BASE_URL}/${encodeURIComponent(trimmed)}`;
};

const getPrizeImageUrl = (prizeType, prizeLabel, prizeName) => {
  const typeKey = normalizeKey(prizeType).replace(/ /g, '_');
  const direct = PRIZE_IMAGES[prizeType] || PRIZE_IMAGES[typeKey];
  const labelKey = normalizeKey(prizeLabel);
  const nameKey = normalizeKey(prizeName);
  const filename = direct || LABEL_IMAGE_MAP[labelKey] || LABEL_IMAGE_MAP[nameKey];
  if (!filename) return null;
  return `${IMAGE_BASE_URL}/${encodeURIComponent(filename)}`;
};

const Game = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t, tPrize } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState(null);
  const [playMode, setPlayMode] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [currentPrizeIndex, setCurrentPrizeIndex] = useState(0);
  const [revealedPrizes, setRevealedPrizes] = useState([]);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const timeoutRef = useRef(null);
  const finalizeTimeoutRef = useRef(null);

  const verifyToken = useCallback(async () => {
    try {
      const response = await api.get(`/game/verify/${token}`);
      if (response.data.valid) {
        setPlayMode(response.data.play_mode || null);
        if (response.data.predetermined_prizes?.length > 0) {
          setPrizes(response.data.predetermined_prizes);
          const firstUnrevealed = response.data.predetermined_prizes.findIndex(p => !p.revealed);
          if (firstUnrevealed >= 0) setCurrentPrizeIndex(firstUnrevealed);
          const alreadyRevealed = response.data.predetermined_prizes
            .filter(p => p.revealed)
            .map(p => ({ ...p, revealed_at: p.played_at }));
          setRevealedPrizes(alreadyRevealed);
        }
      } else {
        setError(response.data.error || 'Token invalide');
      }
    } catch (err) {
      setError(t('tokenVerifyError'));
    } finally {
      setValidating(false);
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    verifyToken();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (finalizeTimeoutRef.current) clearTimeout(finalizeTimeoutRef.current);
    };
  }, [token, verifyToken]);

  const handleRevealComplete = () => {
    setIsRevealed(true);
    const currentPrize = prizes[currentPrizeIndex];
    markPrizeAsPlayed();

    const commitReveal = () => {
      setRevealedPrizes(prev => [...prev, { ...currentPrize, revealed_at: new Date().toISOString() }]);
    };

    const isLastCard = currentPrizeIndex >= prizes.length - 1;
    if (isLastCard) {
      finalizeTimeoutRef.current = setTimeout(commitReveal, FINAL_REVEAL_HOLD_MS);
      return;
    }
    commitReveal();
  };

  const markPrizeAsPlayed = async () => {
    try {
      await api.post(`/game/mark-played/${token}`, { play_number: prizes[currentPrizeIndex].play_number });
    } catch (err) {
      console.error('Erreur marquage lot joué:', err);
    }
  };

  useEffect(() => {
    if (isRevealed && currentPrizeIndex < prizes.length - 1) {
      setIsResetting(true);
      timeoutRef.current = setTimeout(() => {
        setCurrentPrizeIndex(prev => prev + 1);
        setIsRevealed(false);
        setIsResetting(false);
      }, 3000);
    }
  }, [isRevealed, currentPrizeIndex, prizes.length]);

  const currentPrize = prizes[currentPrizeIndex];
  const allRevealed = revealedPrizes.length === prizes.length && prizes.length > 0;
  const remainingCards = prizes.length - revealedPrizes.length;

  const isWinnerPrize = (prize) => {
    if (!prize.prize_code) return false;
    const losingTypes = ['try_again', 'reessayer', 'perdu', 'lose'];
    return !losingTypes.some(type => prize.prize_type.toLowerCase().includes(type));
  };

  const getPrizeThumbUrl = (prize) => {
    if (!prize) return null;
    const iconUrl = normalizeAssetUrl(prize.icon);
    if (iconUrl) return iconUrl;
    return getPrizeImageUrl(prize.prize_type, prize.prize_label, prize.name || prize.prize_name);
  };

  const winningPrizes = revealedPrizes.filter(isWinnerPrize);
  const hasWinningPrizes = winningPrizes.length > 0;

  if (loading || validating) {
    return (
      <div className="game-page-container">
        <div className="game-loading-container">
          <div className="game-spinner"></div>
          <p className="game-loading-text">{t('loadingCard')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    const translatedError = error.includes('déjà utilisé') ? t('alreadyUsed')
      : error.includes('expiré') ? t('expired')
      : t('tokenVerifyError');
    return (
      <div className="game-page-container">
        <div className="game-error-container">
          <div className="game-error-icon">❌</div>
          <h2 className="game-error-title">{t('error')}</h2>
          <p className="game-error-message">{translatedError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="game-page-container">
      <nav className="game-navbar">
        <div className="game-navbar-content">
          {playMode === 'direct' && (
            <button onClick={() => navigate('/hostess')} className="game-back-button">
              <i className="fas fa-arrow-left me-2"></i>{t('back')}
            </button>
          )}
          <img src="/logo-white.png" alt="Reaktor Nature Power" className="game-logo" />
          <div style={{ position: 'absolute', right: '16px', display: 'flex', alignItems: 'center' }}>
            <LangToggle />
          </div>
        </div>
      </nav>

      <div className="game-content">
        {!allRevealed && currentPrize && (
          <>
            <div className="game-card game-instructions-card">
              <div className="game-card-header">
                <div className="game-card-icon">
                  <i className="fas fa-ticket-alt"></i>
                </div>
                <div className="game-card-title-group">
                  <h2 className="game-card-title">{t('scratchTitle')}</h2>
                  <p className="game-card-subtitle">{t('scratchSubtitle')}</p>
                </div>
              </div>
              {remainingCards > 0 && (
                <div className="game-progress-bar">
                  <div className="game-progress-info">
                    <span className="game-progress-label">{t('cardLabel')} {currentPrizeIndex + 1} {t('cardOf')} {prizes.length}</span>
                    <span className="game-progress-remaining">{remainingCards > 1 ? `${remainingCards} ${t('cardsRemaining')}` : t('lastCard')}</span>
                  </div>
                  <div className="game-progress-track">
                    <div
                      className="game-progress-fill"
                      style={{ width: `${((revealedPrizes.length + (isRevealed ? 1 : 0)) / prizes.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <div className="game-scratch-wrapper">
              <ScratchCard
                key={currentPrizeIndex}
                prize={{
                  name: currentPrize.prize_label,
                  prize_label: currentPrize.prize_label,
                  prize_type: currentPrize.prize_type,
                  prize_code: currentPrize.prize_code,
                  image_url: getPrizeImageUrl(currentPrize.prize_type, currentPrize.prize_label, currentPrize.name),
                  icon: currentPrize.icon
                }}
                onReveal={handleRevealComplete}
                isRevealed={isRevealed}
              />
            </div>

            {isResetting && (
              <div className="game-reset-message">
                <div className="game-reset-icon">🎉</div>
                <p>{t('preparingNext')}</p>
              </div>
            )}
          </>
        )}

        {revealedPrizes.length > 0 && (
          <div className="game-card game-history-card">
            <div className="game-card-header">
              <div className="game-card-icon game-icon-gold">
                <i className="fas fa-trophy"></i>
              </div>
              <div className="game-card-title-group">
                <h3 className="game-card-title">{t('revealedPrizes')}</h3>
                <p className="game-card-subtitle">{revealedPrizes.length} / {prizes.length}</p>
              </div>
            </div>
            <div className="game-history-list">
              {revealedPrizes.map((prize, idx) => {
                const isWinner = isWinnerPrize(prize);
                const thumbUrl = isWinner ? getPrizeThumbUrl(prize) : null;
                return (
                  <div key={idx} className={`game-history-item ${isWinner ? 'winner' : 'loser'}`}>
                    <div className={`game-history-number ${isWinner ? 'winner' : 'loser'}`}>
                      #{prize.play_number}
                    </div>
                    <div className="game-history-content">
                      <div className="game-history-label">{isWinner ? tPrize(prize.prize_label) : t('loseLabel')}</div>
                      {prize.prize_code && (
                        <div className="game-history-code">{t('codeLabel')}: {prize.prize_code}</div>
                      )}
                    </div>
                    <div className={`game-history-icon ${isWinner ? 'winner' : 'loser'}`}>
                      {isWinner && thumbUrl ? (
                        <img src={thumbUrl} alt={prize.prize_label || 'Lot'} loading="lazy" />
                      ) : isWinner ? '🎁' : '😢'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {allRevealed && (
          <div className={`game-card game-complete-card ${hasWinningPrizes ? 'winner' : 'loser'}`}>
            {hasWinningPrizes ? (
              <>
                <div className="game-complete-icon winner">
                  <i className="fas fa-glass-cheers"></i>
                </div>
                <h2 className="game-complete-title winner">{t('congratulations')}</h2>
                <p className="game-complete-text">{t('youWon')} {winningPrizes.length} {winningPrizes.length > 1 ? t('lotPlural') : t('lot')} !</p>
                <div className="game-winning-list">
                  {winningPrizes.map((prize, idx) => {
                    const thumbUrl = getPrizeThumbUrl(prize);
                    return (
                      <div key={idx} className="game-winning-item">
                        <span className="game-winning-icon">
                          {thumbUrl ? <img src={thumbUrl} alt={prize.prize_label || 'Lot'} loading="lazy" /> : '🎁'}
                        </span>
                        <span className="game-winning-label">{tPrize(prize.prize_label)}</span>
                        {prize.prize_code && (
                          <span className="game-winning-code">{t('codeLabel')}: {prize.prize_code}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="game-complete-action winner">
                  <i className="fas fa-gift me-2"></i>
                  {t('presentCodes')}
                </div>
              </>
            ) : (
              <>
                <div className="game-complete-icon loser">
                  <i className="fas fa-heart-broken"></i>
                </div>
                <h2 className="game-complete-title loser">{t('tooLate')}</h2>
                <p className="game-complete-text">{t('noWin')}</p>
                <div className="game-complete-action loser">
                  <i className="fas fa-redo me-2"></i>
                  {t('tryAgain')}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <footer className="game-footer">
        <p>© 2026 Reaktor Nature Power</p>
      </footer>
    </div>
  );
};

export default Game;
